// FrontEnd/src/utils/inactivityTracker.ts
// Inactivity tracker for session timeout

import { clearSession } from './session';

let inactivityTimer: NodeJS.Timeout | null = null;
let warningTimer: NodeJS.Timeout | null = null;
let isTracking = false;
let timeoutSeconds = 300; // 5 minutes (300 seconds)
let onTimeoutCallback: (() => void) | null = null;
let onWarningCallback: ((secondsLeft: number) => void) | null = null;
let lastActivityTime = Date.now();

// Throttle activity detection to avoid excessive timer resets
let throttleTimer: NodeJS.Timeout | null = null;
const THROTTLE_DELAY = 1000; // Reset timer max once per second

/**
 * Reset the inactivity timer
 */
const resetTimer = () => {
  if (!isTracking) return;

  // Clear existing timers
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }
  if (warningTimer) {
    clearTimeout(warningTimer);
  }

  lastActivityTime = Date.now();

  // Update last activity in localStorage for cross-tab sync
  localStorage.setItem('last_activity', lastActivityTime.toString());

  // Set warning timer (5 seconds before timeout)
  const warningTime = (timeoutSeconds - 5) * 1000;
  if (warningTime > 0) {
    warningTimer = setTimeout(() => {
      if (onWarningCallback) {
        onWarningCallback(5);
      }
    }, warningTime);
  }

  // Set inactivity timeout
  inactivityTimer = setTimeout(() => {
    handleTimeout();
  }, timeoutSeconds * 1000);
};

/**
 * Handle timeout - clear session and redirect
 */
const handleTimeout = () => {
  console.log('Session timeout due to inactivity');
  clearSession();
  
  // Clear activity tracking
  localStorage.removeItem('last_activity');
  
  if (onTimeoutCallback) {
    onTimeoutCallback();
  } else {
    // Default: redirect to login
    window.location.href = '/login';
  }
};

/**
 * Throttled activity handler
 */
const handleActivity = () => {
  if (!isTracking) return;

  // Throttle: only reset timer once per second
  if (throttleTimer) {
    return;
  }

  throttleTimer = setTimeout(() => {
    resetTimer();
    throttleTimer = null;
  }, THROTTLE_DELAY);
};

/**
 * Listen for activity from other tabs
 */
const setupCrossTabActivitySync = () => {
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === 'last_activity' && e.newValue) {
      const otherTabActivityTime = parseInt(e.newValue, 10);
      const currentTime = Date.now();
      const timeSinceActivity = currentTime - otherTabActivityTime;

      // If other tab had activity recently, reset our timer
      if (timeSinceActivity < timeoutSeconds * 1000) {
        resetTimer();
      }
    }
  };

  window.addEventListener('storage', handleStorageChange);

  // Also check periodically for cross-tab activity
  const checkInterval = setInterval(() => {
    if (!isTracking) {
      clearInterval(checkInterval);
      return;
    }

    const storedActivity = localStorage.getItem('last_activity');
    if (storedActivity) {
      const activityTime = parseInt(storedActivity, 10);
      const timeSinceActivity = Date.now() - activityTime;

      // If activity was recent, reset timer
      if (timeSinceActivity < timeoutSeconds * 1000 && activityTime > lastActivityTime) {
        resetTimer();
      }
    }
  }, 2000); // Check every 2 seconds

  return () => {
    window.removeEventListener('storage', handleStorageChange);
    clearInterval(checkInterval);
  };
};

/**
 * Start inactivity tracking
 */
export const startInactivityTracker = (
  timeout: number = 300, // Default: 5 minutes (300 seconds)
  onTimeout?: () => void,
  onWarning?: (secondsLeft: number) => void
): (() => void) => {
  if (isTracking) {
    stopInactivityTracker();
  }

  timeoutSeconds = timeout;
  onTimeoutCallback = onTimeout || null;
  onWarningCallback = onWarning || null;
  isTracking = true;

  // Activity event listeners
  const events = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'keydown',
  ];

  events.forEach((event) => {
    document.addEventListener(event, handleActivity, true);
  });

  // Window focus event
  window.addEventListener('focus', handleActivity);

  // Start the timer
  resetTimer();

  // Setup cross-tab sync
  const cleanupCrossTab = setupCrossTabActivitySync();

  // Return cleanup function
  return () => {
    stopInactivityTracker();
    cleanupCrossTab();
  };
};

/**
 * Stop inactivity tracking
 */
export const stopInactivityTracker = (): void => {
  isTracking = false;

  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }

  if (warningTimer) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }

  if (throttleTimer) {
    clearTimeout(throttleTimer);
    throttleTimer = null;
  }

  // Remove event listeners
  const events = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'keydown',
  ];

  events.forEach((event) => {
    document.removeEventListener(event, handleActivity, true);
  });

  window.removeEventListener('focus', handleActivity);

  // Clear activity tracking
  localStorage.removeItem('last_activity');
};

/**
 * Pause inactivity tracking (e.g., during API calls)
 */
export const pauseInactivityTracker = (): void => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
  if (warningTimer) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }
};

/**
 * Resume inactivity tracking
 */
export const resumeInactivityTracker = (): void => {
  if (isTracking) {
    resetTimer();
  }
};

/**
 * Reset inactivity timer manually (e.g., when user clicks "Stay logged in")
 */
export const resetInactivityTimer = (): void => {
  if (isTracking) {
    resetTimer();
  }
};

/**
 * Get time remaining until timeout (in seconds)
 */
export const getTimeRemaining = (): number => {
  if (!isTracking || !inactivityTimer) {
    return 0;
  }

  const timeSinceActivity = Date.now() - lastActivityTime;
  const remaining = timeoutSeconds - Math.floor(timeSinceActivity / 1000);
  return Math.max(0, remaining);
};

